import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  TextField,
  Typography,
  Divider,
} from "@mui/material";
import { Delete as DeleteIcon } from "@mui/icons-material";
import {
  IProductForOrder,
  ISeederOrderItem,
  IDeliveryMethod,
} from "../../types/seederOrders";

interface OrderItem extends ISeederOrderItem {
  product: IProductForOrder;
}

interface OrderSummaryProps {
  items: OrderItem[];
  onUpdateAmount: (productId: number, amount: number) => void;
  onRemoveItem: (productId: number) => void;
  subtotal: number;
  deliveryFee: number;
  deliveryMethod: IDeliveryMethod | null;
  total: number;
}

const OrderSummary = ({
  items,
  onUpdateAmount,
  onRemoveItem,
  subtotal,
  deliveryFee,
  deliveryMethod,
  total,
}: OrderSummaryProps) => {
  const formatCurrency = (value: number) => {
    return value.toLocaleString("pt-BR", {
      style: "currency",
      currency: "BRL",
    });
  };

  const calculateItemTotal = (item: OrderItem) => {
    return parseFloat(item.product.price) * item.amount;
  };

  return (
    <Box>
      <TableContainer component={Paper} variant="outlined">
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Produto</TableCell>
              <TableCell>Produtor</TableCell>
              <TableCell align="right">Preço Unit.</TableCell>
              <TableCell align="center">Quantidade</TableCell>
              <TableCell align="right">Total</TableCell>
              <TableCell align="center">Ações</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {items.map((item) => (
              <TableRow key={item.product_id}>
                <TableCell>{item.product.base_product_name}</TableCell>
                <TableCell>{item.product.producer_name}</TableCell>
                <TableCell align="right">
                  {formatCurrency(parseFloat(item.product.price))}
                </TableCell>
                <TableCell align="center">
                  <TextField
                    type="number"
                    size="small"
                    value={item.amount}
                    onChange={(e) => {
                      const newAmount = parseInt(e.target.value) || 1;
                      const maxAmount = item.product.unlimited_stock
                        ? newAmount
                        : Math.min(newAmount, item.product.stock);
                      onUpdateAmount(item.product_id, Math.max(1, maxAmount));
                    }}
                    inputProps={{
                      min: 1,
                      max: item.product.unlimited_stock
                        ? undefined
                        : item.product.stock,
                    }}
                    sx={{ width: 80 }}
                  />
                </TableCell>
                <TableCell align="right">
                  <Typography fontWeight="medium">
                    {formatCurrency(calculateItemTotal(item))}
                  </Typography>
                </TableCell>
                <TableCell align="center">
                  <IconButton
                    size="small"
                    color="error"
                    onClick={() => onRemoveItem(item.product_id)}
                  >
                    <DeleteIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Box sx={{ mt: 2, p: 2, bgcolor: "grey.100", borderRadius: 1 }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
          <Typography variant="body2">
            Total de itens: {items.reduce((sum, item) => sum + item.amount, 0)}
          </Typography>
        </Box>
        <Divider sx={{ my: 1 }} />

        <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
          <Typography>Subtotal:</Typography>
          <Typography>{formatCurrency(subtotal)}</Typography>
        </Box>

        {deliveryMethod && (
          <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
            <Typography>Taxa de entrega ({deliveryMethod.title}):</Typography>
            <Typography>{formatCurrency(deliveryFee)}</Typography>
          </Box>
        )}

        <Divider sx={{ my: 1 }} />

        <Box sx={{ display: "flex", justifyContent: "space-between" }}>
          <Typography variant="h6" fontWeight="bold">
            Total:
          </Typography>
          <Typography variant="h6" color="primary" fontWeight="bold">
            {formatCurrency(total)}
          </Typography>
        </Box>

        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ mt: 1, display: "block" }}
        >
          * Descontos por crédito serão aplicados automaticamente
        </Typography>
      </Box>
    </Box>
  );
};

export default OrderSummary;
